import { useEffect, useRef, useState } from 'react';

const DEFAULT_DURATION = 600;

const easeOutCubic = (progress: number): number => 1 - (1 - progress) ** 3;

const prefersReducedMotion = (): boolean => {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Animates a number from its previous value to a new target value using
 * requestAnimationFrame with an easeOutCubic easing curve.
 *
 * Respects the user's reduced-motion preference by snapping instantly.
 *
 * @param target - The value to animate towards
 * @param duration - Animation duration in milliseconds (default: 600ms)
 * @returns The current animated value
 */
export const useCountUp = (
    target: number,
    duration = DEFAULT_DURATION,
): number => {
    const [value, setValue] = useState(target);
    const frameRef = useRef<number | null>(null);
    const startValueRef = useRef(target);

    useEffect(() => {
        if (prefersReducedMotion()) {
            setValue(target);

            return undefined;
        }

        const startValue = startValueRef.current;
        const delta = target - startValue;

        if (delta === 0) {
            return undefined;
        }

        let startTime: number | null = null;

        const step = (timestamp: number): void => {
            if (startTime === null) {
                startTime = timestamp;
            }

            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = easeOutCubic(progress);
            const next = startValue + delta * eased;

            setValue(next);

            if (progress < 1) {
                frameRef.current = requestAnimationFrame(step);
            } else {
                startValueRef.current = target;
            }
        };

        frameRef.current = requestAnimationFrame(step);

        return () => {
            if (frameRef.current !== null) {
                cancelAnimationFrame(frameRef.current);
            }

            startValueRef.current = target;
        };
    }, [target, duration]);

    return value;
};
